import { isValidUrl, useDebounce } from "@api";
import { InputAdornment, type TextFieldProps, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import type React from "react";
import { useCallback, useMemo } from "react";
import { StyledTextField } from "./styles";

export type IUrlInputProps = TextFieldProps & {
  url: string;
  onUrlChange: (url: string) => void;
  onSearch: (url: string) => void;
  onCancelSearch: () => void;
  loading: boolean;
  /** `true` once a lookup for the current `url` has settled (found or not). */
  searchDone: boolean;
  /** Whether the lookup found an existing offer. */
  found: boolean;
};

export const UrlInput = ({
  url,
  onUrlChange,
  onSearch,
  onCancelSearch,
  loading,
  searchDone,
  found,
  ...props
}: IUrlInputProps) => {
  const handleOfferInputDebounce = useDebounce(
    (value: string) => {
      if (value && isValidUrl(value)) {
        onSearch(value);
      }
    },
    700,
    [onSearch]
  );

  const handleUrlChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = evt.target.value.trim();
      onUrlChange(value);
      if (loading) {
        onCancelSearch();
      }
      handleOfferInputDebounce(value);
    },
    [onUrlChange, handleOfferInputDebounce, loading, onCancelSearch]
  );

  const helperText = useMemo(() => {
    if (url) {
      if (!isValidUrl(url)) {
        return (
          <Typography sx={(theme) => ({ color: theme.palette.danger.main })}>
            Attention, cette URL n&apos;est pas valide.
          </Typography>
        );
      }
      if (found && searchDone) {
        return (
          <Typography sx={(theme) => ({ color: theme.palette.success.main })}>
            Bonne nouvelle, cette offre a été trouvée sur Horizon ! Les champs ont donc été automatiquement pré-remplis.
          </Typography>
        );
      }
      if (!found && searchDone) {
        return (
          <Typography>Cette offre n&apos;existe pas sur Horizon, vous pouvez remplir les champs ci-dessous.</Typography>
        );
      }
    }
    return;
  }, [url, searchDone, found]);

  return (
    <StyledTextField
      {...props}
      id="offer-url-input"
      label="URL de l'offre"
      placeholder="URL de l'offre"
      value={url}
      type="text"
      onChange={handleUrlChange}
      size="small"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {!url ? null : loading ? <CircularProgress size={20} /> : null}
          </InputAdornment>
        ),
      }}
      helperText={helperText}
      disabled={found}
    />
  );
};
